import React, {useEffect, useRef} from 'react';
import {Grid, TextField, Typography} from '@mui/material';
import {PlanProviderSelectOption} from '../../../hooks/usePlanNamesProviders';
import type {newPlanParentFields} from './builderSetUpNewFields';
import type {modelPortfolioField} from '../BuilderModify/builderModifyFieldTypes';
import {BuilderMandatesWrapper as MandatesWrapper} from '../pages/BuilderMandatesModal';
import {BuilderPlanFeesField} from '../CreateBuilder/BuilderPlanFees';
import {builderFieldsType} from '../CreateBuilder/builderFields';
import {ShowIfDebugWrapper} from '../../../contexts/DebugProvider';
import {useAutoField, useAutoForm} from '../../../components/AutoForm/useAutoForm';
import {UPDATES} from '../../../shared/UPDATES';
import {useShowMandateSelector} from '../hooks/builderSetUpNewHooks';
import {
  isBondPlan,
  NO_SALTUS_GIA_GROSS_MESSAGE,
  useBondSubplanProvider,
} from './bondSetUpNew';

export interface BuilderSubPlanProps {
  formikKeyPrefix: string;
  productProviders: PlanProviderSelectOption[] | undefined;
}

export const BuilderNewSubPlanDetails = ({
  formikKeyPrefix,
  productProviders,
}: BuilderSubPlanProps) => {
  const {values} = useAutoForm<builderFieldsType>();
  const [parentProductIdField] = useAutoField<newPlanParentFields['productNameId']>(
    'setUpNew.parent.productNameId'
  );
  const [parentPlanProviderField] = useAutoField<newPlanParentFields['planProvider']>(
    'setUpNew.parent.planProvider'
  );
  const [, , subplanPlanProviderHelpers] = useAutoField<string | undefined>(
    `${formikKeyPrefix}.planProvider`
  );
  const [subplanProductNameIdField, , subplanProductNameIdHelpers] = useAutoField<
    string | undefined
  >(`${formikKeyPrefix}.productNameId`);

  const [mandateField, , mandateHelpers] = useAutoField<modelPortfolioField | undefined>(
    `${formikKeyPrefix}.model`
  );

  const planProvider: string | null = parentPlanProviderField.value || null;
  const enableMandateField =
    !!parentPlanProviderField.value || !!parentProductIdField.value;

  const showMandateSelector = useShowMandateSelector();

  const isBond = isBondPlan(values.mainPlanType);
  const saltusGiaGrossProvider = useBondSubplanProvider(isBond);
  const subplanProviderId = isBond
    ? saltusGiaGrossProvider?.value
    : parentPlanProviderField.value;

  useEffect(() => {
    subplanPlanProviderHelpers.setValue(UPDATES._D532, subplanProviderId ?? '', true);
  }, [subplanProviderId]);

  // A bond portfolio is Saltus-only, so drop it when the plan type moves on or off bond.
  const previousIsBond = useRef(isBond);
  useEffect(() => {
    if (previousIsBond.current !== isBond) {
      mandateHelpers.setValue(UPDATES._D534, undefined, true);
    }
    previousIsBond.current = isBond;
  }, [isBond]);

  useEffect(() => {
    subplanProductNameIdHelpers.setValue(
      UPDATES._D533,
      parentProductIdField.value ?? '',
      true
    );
  }, [parentProductIdField.value]);

  if (isBond && saltusGiaGrossProvider === undefined) {
    return (
      <Grid pt={2} container>
        <Typography>Loading subplan providers...</Typography>
      </Grid>
    );
  }

  if (isBond && !saltusGiaGrossProvider) {
    return (
      <Grid pt={2} container>
        <Typography color="error">{NO_SALTUS_GIA_GROSS_MESSAGE}</Typography>
      </Grid>
    );
  }

  return (
    <Grid pt={2} container direction="row" spacing={2}>
      {isBond && (
        <Grid item xs={6}>
          <TextField
            fullWidth
            disabled
            label="Subplan provider"
            value={saltusGiaGrossProvider?.label ?? ''}
          />
        </Grid>
      )}
      <Grid item xs={6}>
        <MandatesWrapper
          isChild
          showCurrentMandate={false}
          planProviderOptions={
            isBond && saltusGiaGrossProvider
              ? [saltusGiaGrossProvider]
              : productProviders || []
          }
          forceMandateSelector={isBond}
          formikKeyPrefix={formikKeyPrefix}
          disabledReason={
            !enableMandateField
              ? 'Select a parent plan provider first'
              : !subplanProductNameIdField.value
              ? 'Select a product type first'
              : undefined
          }
        />
      </Grid>
      <Grid item xs={12}>
        <ShowIfDebugWrapper>
          FormikKeyPrefix: {formikKeyPrefix}
          <br />
          parentProductIdField: {parentProductIdField.value}
        </ShowIfDebugWrapper>

        <BuilderPlanFeesField
          inModify={false}
          hasSubplan={false}
          requiresMandate={isBond || showMandateSelector}
          mandateId={
            mandateField.value && typeof mandateField.value === 'object'
              ? mandateField.value?.id
              : undefined
          }
          providerId={planProvider}
          productId={parentProductIdField.value} // just for the time being
          formikKeyPrefixWithoutFee={formikKeyPrefix}
          parentProductId={parentProductIdField.value}
        />
      </Grid>
    </Grid>
  );
};
